import React, {useMemo} from 'react';
import {classes, style} from './doppe-action-type-common-list-view-item.st.css';
import {
    ClampedText,
    cssEllipsis,
    flexColumn,
    Icon,
    RatioMaintainerIgnoreClassName,
    reactCreateMemo,
    RichTextContent
} from '@wix/devzai-utils-react';
import {CommonClasses, FlexLayout} from '@wix/devzai-common-client';
import {
    arrayFirst,
    asArray,
    dateResolveFromDateTimeValue,
    evaluateFunction,
    SerializedDateTimeValueAsString
} from '@wix/devzai-utils-common';
import {WixMediaResource} from '@wix/devzai-common-wix';
import {
    DoppeActionLayout,
    doppeActionLayoutCanHideDescription,
    doppeActionLayoutCanHideImage,
    doppeActionLayoutCanHideTitle,
    doppeActionLayoutSupportsDescription,
    DoppeActionListItemClickBehaviour,
    DoppeListActionSettings
} from '../../../../client-server-common';
import {
    DoppeActionTypeSpeedDialWidgetIconChevronLeft,
    DoppeActionTypeSpeedDialWidgetIconChevronRight,
    DoppeActionTypeSpeedDialWidgetIconOpenLink
} from '../../../doppe-action-type-speed-dial/doppe-action-type-speed-dial-widget/doppe-action-type-speed-dial-widget-icons';
import {DoppePageMediaView} from '../../../../components/doppe-page-media-view/doppe-page-media-view';
import {
    doppeViewerIntlDateTimeFormatShortMonthAndDay
} from '../../../../client-server-common/doppe-viewer/doppe-viewer-intl-utils/doppe-viewer-intl-utils';
import {
    DoppeRichTextContentType
} from '../../../../client-server-common/doppe-tiptap-extensions/doppe-tiptap-extensions';
import {WidgetRenderingContextType} from '../../../../client-server-common/doppe-action-types/doppe-action-type';
import IconChevronDown
    from '../../../../components/doppe-action-button-templates/doppe-standard-action-button-template/icons/ChevronDown.svg';
import {IDoppeViewerActionContext} from '../../../../doppe-sdk';
import {
    doppeViewerActionResolveListItemClickBehaviour
} from '../../../../doppe-sdk/doppe-viewer-action-context/doppe-viewer-action-context-utils';
import {
    DoppeViewerRichTextView,
    DoppeViewerRichTextViewHtmlRenderingMode
} from '../../../../components/doppe-viewer-rich-text-view/doppe-viewer-rich-text-view';

import {
    useDoppeViewerLang,
    useDoppViewerIsRtl
} from '../../../../components/doppe-viewer-bymo-page-props-react/doppe-viewer-bymo-page-props-react';
import {
    useAssertWidgetRenderingContextType
} from '../../../../components/doppe-viewer-widget-rendering-context/doppe-viewer-widget-rendering-context';
import {
    DoppeActionTypeBaseListViewItem
} from '../doppe-action-type-base-list-view-item/doppe-action-type-base-list-view-item';

const WidgetRenderingContextTypeCssState = {
    InPage: 'inPage',
    Modal: 'modal',
}

export const DoppeActionTypeCommonListViewItem = reactCreateMemo(function DoppeActionTypeListViewItem<PROPS extends DoppeListActionSettings.WithItemClickBehaviour>(
    props: {
        title: string;
        description: RichTextContent;
        image: WixMediaResource[] | WixMediaResource | null;
        price?: React.ReactNode;
        date?: SerializedDateTimeValueAsString;
        textOnImage?: string;
        layout: DoppeActionLayout;
        gridImageRatio?: number;
        centeredIcon?: Icon.Src;
        isOneItem: boolean;
        ribbon?: string;
        hideImageWhenPossible?: boolean;
        hideTitleWhenPossible?: boolean;
        hideDescriptionWhenPossible?: boolean;
        actionContext?: IDoppeViewerActionContext<PROPS>;
        innerItemIndex: number;
        innerItemId: string;
    }
) {
    const {
        image,
        price,
        date,
        textOnImage,
        title,
        description,
        layout,
        gridImageRatio,
        centeredIcon,
        ribbon,
        isOneItem = false,
        hideImageWhenPossible = false,
        hideTitleWhenPossible = false,
        hideDescriptionWhenPossible = false,
        actionContext,
        innerItemIndex,
        innerItemId,
    } = props;

    const isLink = actionContext && doppeViewerActionResolveListItemClickBehaviour(actionContext) === DoppeActionListItemClickBehaviour.OpenLink;

    const isColumn = layout === DoppeActionLayout.Column;
    const doppeViewerLang = useDoppeViewerLang();
    const calculatedImage = useMemo(() => arrayFirst(asArray(image)) ?? null, [image]);

    const widgetRenderingContextType = useAssertWidgetRenderingContextType();

    const isRtl = useDoppViewerIsRtl();

    const mediaViewLayoutSpec = useMemo(() => {
        switch (layout) {
            case DoppeActionLayout.Column: {
                return {widthHeightRatio: 1};
            }
            case DoppeActionLayout.Grid: {
                return {widthHeightRatio: gridImageRatio ?? 1.2};
            }
            case DoppeActionLayout.ImagesGrid: {
                return {widthHeightRatio: 1};
            }
            case DoppeActionLayout.LargeColumn: {
                return {widthHeightRatio: gridImageRatio ?? 1.5};
            }
            case DoppeActionLayout.Swiper: {
                return {widthHeightRatio: gridImageRatio ?? 1.2};
            }
            case DoppeActionLayout.Carousel: {
                return {widthHeightRatio: gridImageRatio ?? 1.2};
            }
            case DoppeActionLayout.CarouselSmall: {
                return {widthHeightRatio: gridImageRatio ?? 1.2};
            }
        }
    }, [layout, gridImageRatio]);
    const mediaViewWidthSpec = useMemo(() => {
        switch (layout) {
            case DoppeActionLayout.Column: {
                return {width: 60}
            }
            case DoppeActionLayout.Grid: {
                return {contentRelativeWidth: 0.37}
            }
            case DoppeActionLayout.ImagesGrid: {
                return {contentRelativeWidth: 0.3}
            }
            case DoppeActionLayout.LargeColumn: {
                return {contentRelativeWidth: 0.8}
            }
            case DoppeActionLayout.Swiper: {
                return {contentRelativeWidth: 0.8}
            }
            case DoppeActionLayout.Carousel: {
                return {contentRelativeWidth: 0.7}
            }
            case DoppeActionLayout.CarouselSmall: {
                return {contentRelativeWidth: 0.35}
            }
        }
    }, [layout])

    return (
        <DoppeActionTypeBaseListViewItem
            className={style(classes.root, {
                layout: layout,
                isOneItem: isOneItem,
                hasImage: !!calculatedImage,
                widgetRenderingContextType: evaluateFunction(() => {
                    switch (widgetRenderingContextType) {
                        case WidgetRenderingContextType.Modal: {
                            return WidgetRenderingContextTypeCssState.Modal;
                        }
                        case WidgetRenderingContextType.Card: {
                            return WidgetRenderingContextTypeCssState.InPage;
                        }
                        case WidgetRenderingContextType.Strip: {
                            return WidgetRenderingContextTypeCssState.InPage;
                        }
                    }
                }),
            }, flexColumn())}
            actionContext={actionContext}
            innerItemIndex={innerItemIndex}
            innerItemId={innerItemId}
            contentClassName={style(classes.actionTypeListViewItemViewRootContent, isColumn ? FlexLayout.row.alignToCenter : FlexLayout.column.default)}
        >
            {hideImageWhenPossible && doppeActionLayoutCanHideImage(layout) ? null : (
                <div
                    className={style(classes.linkImageContainer)}
                >
                    <DoppePageMediaView
                        mediaResource={calculatedImage}
                        layoutSpec={mediaViewLayoutSpec}
                        widthSpec={mediaViewWidthSpec}
                    />
                    {
                        price && !isColumn ?
                            <div
                                className={style(classes.priceOnImage, {}, RatioMaintainerIgnoreClassName, cssEllipsis())}
                            >
                                {price}
                            </div>
                            :
                            null
                    }
                    {
                        textOnImage ?
                            <div
                                className={style(classes.textOnImage, {}, RatioMaintainerIgnoreClassName, cssEllipsis(), FlexLayout.row.alignToBottom)}
                            >
                                <div
                                    className={style(classes.textOnImageText, {}, cssEllipsis(), FlexLayout.fillRemainingHorizontalSpace)}
                                >
                                    {textOnImage}
                                </div>

                            </div>
                            :
                            date  ?
                                <div
                                    className={style(classes.dateOnImage, {}, RatioMaintainerIgnoreClassName, cssEllipsis(), FlexLayout.row.alignToBottom)}
                                >
                                    <div
                                        className={style(classes.dateText, {}, FlexLayout.fillRemainingHorizontalSpace)}
                                    >
                                        {doppeViewerIntlDateTimeFormatShortMonthAndDay(
                                            doppeViewerLang,
                                            dateResolveFromDateTimeValue(date)
                                        )}
                                    </div>

                                </div>
                                :
                                null
                    }
                    {
                        centeredIcon ?
                            <Icon
                                className={style(classes.ceneteredIcon, {}, RatioMaintainerIgnoreClassName)}
                                icon={centeredIcon}
                            />
                            :
                            null
                    }
                    {
                        ribbon && !isColumn ?
                            <div
                                className={style(classes.ribbonContainer, {dockedToTop: layout === DoppeActionLayout.ImagesGrid}, RatioMaintainerIgnoreClassName, FlexLayout.column.alignToStart)}
                            >
                                <div
                                    className={style(classes.ribbon, {}, CommonClasses.ellipsis)}
                                >
                                    {ribbon}
                                </div>
                            </div>
                            :
                            null
                    }
                </div>
            )}
            {layout !== DoppeActionLayout.ImagesGrid ? (
                <div
                    className={style(classes.itemContent, FlexLayout.column.default, isColumn ? FlexLayout.fillRemainingHorizontalSpace : undefined)}
                >
                    {
                        ribbon && isColumn ?
                            <div
                                className={style(classes.inlineRibbonContainer, {}, FlexLayout.column.alignToStart)}
                            >
                                <div
                                    className={style(classes.inlineRibbon, {}, CommonClasses.ellipsis)}
                                >
                                    {ribbon}
                                </div>
                            </div>
                            :
                            null
                    }
                    {hideTitleWhenPossible && doppeActionLayoutCanHideTitle(layout) ? null : (
                        <div
                            className={style(classes.itemProductTitle, isColumn ? CommonClasses.ellipsis : CommonClasses.lineClamp(2))}
                        >
                            {title}
                        </div>
                    )}
                    {!doppeActionLayoutSupportsDescription(layout) || (doppeActionLayoutCanHideDescription(layout) && hideDescriptionWhenPossible) || !description ? null : (
                        <>
                            <ClampedText
                                className={style(classes.itemProductDescription, {})}
                                maxLines={isColumn && !!price ? 1 : 2}
                                clampText={true}
                            >
                                {typeof description === 'string' ? description : (
                                    <DoppeViewerRichTextView
                                        richText={description}
                                        richTextContentType={DoppeRichTextContentType.DoppeActionContent}
                                        htmlRenderingMode={DoppeViewerRichTextViewHtmlRenderingMode.PlainText}
                                    />
                                )}
                            </ClampedText>
                        </>
                    )}
                    {
                        price && isColumn ?
                            <div
                                className={style(classes.actualPrice, {})}
                            >
                                {price}
                            </div>
                            :
                            null
                    }
                </div>
            ) : null}
            {isColumn ? (
                <Icon
                    className={style(isLink ? classes.openLinkIcon : classes.openIcon, {})}
                    icon={isLink ? DoppeActionTypeSpeedDialWidgetIconOpenLink : widgetRenderingContextType === WidgetRenderingContextType.Modal ? !isRtl ? DoppeActionTypeSpeedDialWidgetIconChevronRight : DoppeActionTypeSpeedDialWidgetIconChevronLeft : IconChevronDown}
                />
            ) : null}
        </DoppeActionTypeBaseListViewItem>
    );
});


